import usePusher from 'hooks/usePusher';
import { throttle } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IntegrationSlug } from 'types/chift.types';
import showToast from 'utils/toast';
import useInvalidateResources from './useInvalidateResources';

type Event = {
  data?: {
    slug?: IntegrationSlug;
  };
};

const usePeppolIntegrationAutoUpdate = () => {
  const { t } = useTranslation();
  const channel = usePusher();

  const invalidate = useInvalidateResources();

  const invalidatePeppol = useCallback(() => {
    invalidate({
      resource: 'peppolIntegration',
      method: 'invalidate',
      options: {
        cancelRefetch: true,
      },
    });
  }, [invalidate]);

  useEffect(
    function onSuccess() {
      if (!channel) return;

      const handler = throttle((event?: Event) => {
        const slug = event?.data?.slug;
        if (slug !== IntegrationSlug.PEPPOL) return;

        showToast(
          { title: t('user.peppol_integration.successful_propagation') },
          { type: 'info' },
        );

        invalidatePeppol();
      }, 5000);

      channel.bind('user.integration.updated', handler);
      return () => {
        handler.cancel();
        channel.unbind('user.integration.updated', handler);
      };
    },
    [channel, invalidatePeppol],
  );

  useEffect(
    function onFailed() {
      if (!channel) return;

      const handler = throttle((event?: Event) => {
        const slug = event?.data?.slug;
        if (slug !== IntegrationSlug.PEPPOL) return;

        showToast(
          { title: t('user.peppol_integration.failed_propagation') },
          { type: 'info' },
        );

        invalidatePeppol();
      }, 5000);

      channel.bind('user.integration.failed-propagation', handler);
      return () => {
        handler.cancel();
        channel.unbind('user.integration.failed-propagation', handler);
      };
    },
    [channel, invalidatePeppol],
  );
};

export default usePeppolIntegrationAutoUpdate;
